import { defineStore } from 'pinia';
import { ref } from 'vue';
import { profileService, teamsService } from '@/services/erpat';
import { getSessionEpoch } from '@/services/api/erpat-client';

/**
 * Cached identity of the signed-in user (name, department, role, …).
 *
 * Kept fresh by re-fetching whenever a token is issued or rotated — login, MFA,
 * boot restore, and every background/reactive access-token refresh — so screens
 * that greet the user or show their team always reflect the latest server-side
 * profile (see `session.store` + the `tokenRefreshed` handler wired in main.ts).
 *
 * Population is best-effort: a failed fetch leaves the previous values intact
 * rather than blanking the UI, and the two sources are fetched independently so
 * a missing/forbidden teams endpoint never wipes the name.
 *
 * `department` is sourced from the first team the user belongs to
 * (`GET /me/teams`) — the profile payload itself carries no department field.
 */
export const useUserStore = defineStore('user', () => {
  /** The signed-in user's id (ERPat `users.id`) — used to tell self from others. */
  const id = ref('');
  const name = ref('');
  const department = ref('');
  const role = ref('');
  const initials = ref('');
  const email = ref('');
  /** Employment status label (e.g. "Active") — shown as a badge. */
  const status = ref('');
  /** Employment type label (e.g. "Staff") — shown as a badge. */
  const employmentType = ref('');
  /** Employee stage like (e.g. "Regular") — shown as a badge. */
  const employmentStage = ref('');
  /** Absolute avatar URL, or '' when none. Render with an initials fallback. */
  const image = ref('');
  /** True once at least one successful profile fetch has populated the store. */
  const loaded = ref(false);
  /** Bumped on every successful profile load — a change signal consumers can
   *  watch (e.g. to retry a previously-failed avatar image whose URL is unchanged). */
  const version = ref(0);

  // Collapse overlapping refreshes (e.g. a token rotation racing the boot
  // restore, or a 401-retry re-entering through the refresh handler) into one
  // round-trip so we never fire duplicate profile/teams fetches.
  let inFlight: Promise<void> | null = null;

  async function firstTeamTitle(): Promise<string> {
    const { items } = await teamsService.list();
    const first = items[0];
    const title = first && typeof first.title === 'string' ? first.title : '';
    return title;
  }

  /** Re-fetch the user's profile + department. Never rejects. */
  function refresh(): Promise<void> {
    if (inFlight) return inFlight;
    // Capture the session generation before awaiting so a sign-out / expiry that
    // races this fetch can be detected — without it, a fetch that resolves after
    // sign-out would repopulate the previous user's identity onto the guest UI.
    const epoch = getSessionEpoch();
    const run = (async () => {
      const [profile, dept] = await Promise.all([
        profileService.get().catch(() => null),
        firstTeamTitle().catch(() => null),
      ]);
      // Teardown wins: if the session was cleared (or replaced) mid-flight, drop
      // this now-stale result instead of resurrecting a signed-out identity.
      if (getSessionEpoch() !== epoch) return;
      if (profile) {
        id.value = profile.id;
        name.value = profile.name;
        role.value = profile.role;
        initials.value = profile.initials;
        email.value = profile.email;
        status.value = profile.status;
        employmentType.value = profile.employmentType;
        employmentStage.value = profile.employmentStage;
        image.value = profile.image;
        loaded.value = true;
        version.value++;
      }
      // Only overwrite the department when the teams call actually succeeded —
      // a transient failure must not blank a previously-resolved department.
      if (dept !== null) department.value = dept;
    })();
    inFlight = run;
    // Clear the single-flight slot when this run settles, but only if it hasn't
    // already been replaced (e.g. clear() nulled it and a new login started one).
    void run.finally(() => {
      if (inFlight === run) inFlight = null;
    });
    return run;
  }

  /** Drop the cached identity on sign-out. */
  function clear(): void {
    id.value = '';
    name.value = '';
    department.value = '';
    role.value = '';
    initials.value = '';
    email.value = '';
    status.value = '';
    employmentType.value = '';
    employmentStage.value = '';
    image.value = '';
    loaded.value = false;
    // Drop the single-flight slot so a subsequent login starts a fresh fetch
    // instead of awaiting (and writing) the signed-out user's in-flight result.
    inFlight = null;
  }

  return { id, name, department, role, initials, email, status, employmentType, employmentStage, image, loaded, version, refresh, clear };
});
